import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Navigation } from './components/Navigation';
import { isAuthenticated, getUser } from './lib/auth';
import useDeployRefresh from './hooks/useDeployRefresh';
import useSessionKeepAlive from './hooks/useSessionKeepAlive';
import LoginPage from './pages/LoginPage';
import MerchantScanPage from './pages/MerchantScanPage';
import ReportsPage from './pages/ReportsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AdminDashboard from './pages/AdminDashboard';
import SystemHealthPage from './pages/SystemHealthPage';
import UnifiedReportBuilderPage from './pages/UnifiedReportBuilderPage';
import UnifiedWorkflowPage from './pages/UnifiedWorkflowPage';
import TenantManagementPage from './pages/TenantManagementPage';
import UserManagementPage from './pages/UserManagementPage';
import BillingPage from './pages/BillingPage';
import APIKeysPage from './pages/APIKeysPage';
import DataPushPage from './pages/DataPushPage';
import ModuleSyncPage from './pages/ModuleSyncPage';
import PocSignupPage from './pages/PocSignupPage';
import PocAdminPage from './pages/PocAdminPage';

// Keep legacy routes for backward compatibility (hidden from nav)
import CrawlPage from './pages/CrawlPage';
import ProgressPage from './pages/ProgressPage';
import URLValidationPage from './pages/URLValidationPage';
import HybridDashboardPage from './pages/HybridDashboardPage';
import EnterpriseDashboard from './pages/EnterpriseDashboard';
import WorkflowManagement from './pages/WorkflowManagementPage';
import ReportsManagement from './pages/ReportsManagement';
import ReportBuilderPage from './pages/ReportBuilderPage';

const POC_ALLOWED_PREFIXES = ['/scan', '/reports', '/poc-admin'];

function isPocUser() {
  return !!getUser()?.is_poc;
}

function RequireAuth({ children }) {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function PocRestrict({ children }) {
  const location = useLocation();
  if (isPocUser()) {
    const ok = POC_ALLOWED_PREFIXES.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
    );
    if (!ok) {
      return <Navigate to="/scan" replace />;
    }
  }
  return children;
}

function AppLayout({ children }) {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <main className="max-w-[1900px] mx-auto px-4 md:px-6 pt-6 pb-10">
        {children}
      </main>
    </div>
  );
}

function ProtectedRoute({ children }) {
  return (
    <RequireAuth>
      <PocRestrict>
        <AppLayout>{children}</AppLayout>
      </PocRestrict>
    </RequireAuth>
  );
}

function SessionAndDeployGuards() {
  useDeployRefresh();
  useSessionKeepAlive();
  return null;
}

function HomeRedirect() {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  if (isPocUser()) {
    return <Navigate to="/scan" replace />;
  }
  return (
    <ProtectedRoute>
      <AnalyticsPage />
    </ProtectedRoute>
  );
}

function App() {
  return (
    <Router>
      <SessionAndDeployGuards />
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/poc/signup" element={<PocSignupPage />} />

        {/* Protected routes */}
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/scan" element={<ProtectedRoute><MerchantScanPage /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
        <Route path="/report-builder" element={<ProtectedRoute><UnifiedReportBuilderPage /></ProtectedRoute>} />
        <Route path="/workflows" element={<ProtectedRoute><UnifiedWorkflowPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/system-health" element={<ProtectedRoute><SystemHealthPage /></ProtectedRoute>} />
        <Route path="/tenants" element={<ProtectedRoute><TenantManagementPage /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><UserManagementPage /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
        <Route path="/risk-history" element={<Navigate to="/reports" replace />} />
        <Route path="/api-keys" element={<ProtectedRoute><APIKeysPage /></ProtectedRoute>} />
        <Route path="/data-push" element={<ProtectedRoute><DataPushPage /></ProtectedRoute>} />
        <Route path="/module-sync" element={<ProtectedRoute><ModuleSyncPage /></ProtectedRoute>} />
        <Route path="/poc-admin" element={<ProtectedRoute><PocAdminPage /></ProtectedRoute>} />

        {/* Legacy routes (backward compatibility - data retained) */}
        <Route path="/crawl" element={<ProtectedRoute><CrawlPage /></ProtectedRoute>} />
        <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
        <Route path="/url-validation" element={<ProtectedRoute><URLValidationPage /></ProtectedRoute>} />
        <Route path="/hybrid" element={<ProtectedRoute><HybridDashboardPage /></ProtectedRoute>} />
        <Route path="/enterprise" element={<ProtectedRoute><EnterpriseDashboard /></ProtectedRoute>} />
        <Route path="/workflow-management" element={<ProtectedRoute><WorkflowManagement /></ProtectedRoute>} />
        <Route path="/reports-management" element={<ProtectedRoute><ReportsManagement /></ProtectedRoute>} />
        <Route path="/report-builder-legacy" element={<ProtectedRoute><ReportBuilderPage /></ProtectedRoute>} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to={isPocUser() ? '/scan' : '/'} replace />} />
      </Routes>
    </Router>
  );
}

export default App;
